import React, { useState } from "react";

const PrivacyPolicy = () => {
  const [activeSection, setActiveSection] = useState("01");

  const sections = [
    {
      id: "01",
      title: "Information We Collect",
      text: "We may collect personal information that you voluntarily provide to us when you register on the platform, express an interest in obtaining information about us or our courses, or otherwise contact us.",
      points: [
        "Full name and email address",
        "Phone number and educational background",
        "Payment details for course enrollment",
        "Login credentials and account preferences",
        "Device information and IP address",
      ],
    },
    {
      id: "02",
      title: "How We Use Your Information",
      text: "We use the information we collect to provide and improve our educational services and to maintain a secure and personalized learning experience.",
      points: [
        "Provide, operate, and maintain our educational services",
        "Process enrollment and payments securely",
        "Send course updates and notifications",
        "Improve and personalize the platform experience",
        "Respond to questions and provide customer support",
      ],
    },
    {
      id: "03",
      title: "Cookies & Tracking",
      text: "We use cookies and similar tracking technologies to track activity on our website and store certain information. Cookies help us remember your preferences and improve your experience.",
    },
    {
      id: "04",
      title: "Data Sharing & Disclosure",
      text: "We do not sell, trade, or rent your personal information to third parties. We may share your information only when necessary to provide services or comply with legal obligations.",
      points: [
        "Trusted payment and hosting service providers",
        "Legal or regulatory authorities when required",
        "Business transfers such as mergers or acquisitions",
      ],
    },
    {
      id: "05",
      title: "Data Security",
      text: "We implement appropriate technical and organizational security measures to protect your personal information. However, no method of transmission over the internet is completely secure.",
    },
    {
      id: "06",
      title: "Your Rights",
      text: "Depending on your location, you may have certain rights regarding your personal information.",
      points: [
        "Access, update, or delete your personal data",
        "Opt out of marketing communications",
        "Request a copy of the data we hold about you",
        "Withdraw consent at any time",
      ],
    },
    {
      id: "07",
      title: "Children's Privacy",
      text: "Our services are not intended for children under 13 years of age. We do not knowingly collect personal information from children.",
    },
    {
      id: "08",
      title: "Changes to This Policy",
      text: 'We may update this Privacy Policy from time to time. The updated version will be indicated by an updated "Last Updated" date.',
    },
  ];

  const scrollToSection = (id) => {
    setActiveSection(id);

    const element = document.getElementById(`privacy-${id}`);

    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  return (
    <div className="privacy-page">
      <style>{`
        * {
          box-sizing: border-box;
        }

        html {
          scroll-behavior: smooth;
        }

        body {
          margin: 0;
        }

        .privacy-page {
          min-height: 100vh;
          font-family:
            Inter,
            -apple-system,
            BlinkMacSystemFont,
            "Segoe UI",
            sans-serif;
          color: #172033;
          background:
            radial-gradient(
              circle at 90% 0%,
              rgba(99,102,241,.13),
              transparent 28%
            ),
            radial-gradient(
              circle at 0% 35%,
              rgba(124,58,237,.08),
              transparent 25%
            ),
            #f7f8fc;
        }

        /* HEADER */

        .privacy-header {
          position: sticky;
          top: 0;
          z-index: 100;
          height: 76px;
          background: rgba(255,255,255,.86);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border-bottom: 1px solid rgba(226,232,240,.8);
        }

        .header-inner {
          max-width: 1240px;
          height: 100%;
          margin: auto;
          padding: 0 24px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .brand {
          display: flex;
          align-items: center;
          gap: 11px;
          text-decoration: none;
        }

        .brand-mark {
          width: 38px;
          height: 38px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 17px;
          font-weight: 900;
          background:
            linear-gradient(
              135deg,
              #312e81,
              #4f46e5,
              #7c3aed
            );
          box-shadow:
            0 8px 20px rgba(79,70,229,.25);
        }

        .brand-name {
          font-size: 19px;
          font-weight: 900;
          letter-spacing: -.6px;
          color: #111827;
        }

        .brand-name span {
          color: #6366f1;
        }

        .nav {
          display: flex;
          align-items: center;
          gap: 5px;
        }

        .nav a {
          text-decoration: none;
          color: #64748b;
          font-size: 13px;
          font-weight: 700;
          padding: 9px 13px;
          border-radius: 9px;
          transition: .2s;
        }

        .nav a:hover {
          color: #4f46e5;
          background: #eef2ff;
        }

        .nav .nav-button {
          color: white;
          margin-left: 5px;
          padding: 10px 16px;
          background: #111827;
          box-shadow: 0 6px 15px rgba(15,23,42,.14);
        }

        .nav .nav-button:hover {
          color: white;
          background: #312e81;
        }

        /* HERO */

        .hero {
          max-width: 1240px;
          margin: auto;
          padding: 76px 24px 62px;
          text-align: center;
        }

        .hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 7px 13px;
          border-radius: 999px;
          background: rgba(255,255,255,.8);
          border: 1px solid #e5e7eb;
          box-shadow: 0 5px 18px rgba(15,23,42,.05);
          color: #4f46e5;
          font-size: 11px;
          font-weight: 900;
          letter-spacing: 1.3px;
        }

        .badge-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: #6366f1;
          box-shadow: 0 0 0 5px rgba(99,102,241,.10);
        }

        .hero h1 {
          margin: 20px auto 0;
          max-width: 850px;
          font-size: clamp(42px, 6vw, 70px);
          line-height: 1;
          letter-spacing: -4px;
          font-weight: 950;
          color: #111827;
        }

        .hero h1 span {
          background:
            linear-gradient(
              90deg,
              #312e81,
              #4f46e5,
              #7c3aed
            );
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .hero p {
          max-width: 650px;
          margin: 22px auto 0;
          color: #64748b;
          font-size: 16px;
          line-height: 1.8;
        }

        .hero-meta {
          margin-top: 25px;
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 9px;
          color: #94a3b8;
          font-size: 12px;
          font-weight: 700;
        }

        .meta-dot {
          width: 4px;
          height: 4px;
          border-radius: 50%;
          background: #cbd5e1;
        }

        /* MAIN */

        .main {
          max-width: 1180px;
          margin: auto;
          padding: 0 24px 90px;
        }

        .privacy-shell {
          display: grid;
          grid-template-columns: 245px minmax(0, 1fr);
          gap: 28px;
          align-items: start;
        }

        /* SIDEBAR */

        .sidebar {
          position: sticky;
          top: 98px;
          background: rgba(255,255,255,.85);
          border: 1px solid #e5e7eb;
          border-radius: 20px;
          padding: 15px;
          box-shadow:
            0 15px 45px rgba(15,23,42,.055);
        }

        .sidebar-label {
          padding: 7px 10px 12px;
          color: #94a3b8;
          font-size: 10px;
          font-weight: 900;
          letter-spacing: 1.4px;
        }

        .side-item {
          width: 100%;
          border: 0;
          background: transparent;
          display: flex;
          align-items: center;
          gap: 10px;
          text-align: left;
          padding: 10px;
          margin-bottom: 3px;
          border-radius: 10px;
          color: #64748b;
          cursor: pointer;
          font-size: 12px;
          font-weight: 700;
          transition: .2s;
        }

        .side-item:hover {
          background: #f8fafc;
          color: #4f46e5;
        }

        .side-item.active {
          background: #eef2ff;
          color: #4f46e5;
        }

        .side-number {
          font-size: 10px;
          font-weight: 900;
          opacity: .7;
          min-width: 20px;
        }

        /* DOCUMENT */

        .document {
          background: rgba(255,255,255,.94);
          border: 1px solid #e5e7eb;
          border-radius: 28px;
          overflow: hidden;
          box-shadow:
            0 30px 80px rgba(15,23,42,.075),
            0 5px 20px rgba(15,23,42,.025);
        }

        .document-top {
          padding: 25px 34px;
          border-bottom: 1px solid #edf0f4;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .document-label {
          color: #111827;
          font-size: 13px;
          font-weight: 850;
        }

        .document-status {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          padding: 7px 11px;
          border-radius: 999px;
          background: #f0fdf4;
          border: 1px solid #dcfce7;
          color: #15803d;
          font-size: 10px;
          font-weight: 850;
        }

        .status-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #22c55e;
        }

        .document-intro {
          margin: 26px 34px 8px;
          padding: 25px;
          border-radius: 18px;
          background:
            linear-gradient(
              135deg,
              #111827 0%,
              #1e1b4b 55%,
              #312e81 100%
            );
          color: white;
          position: relative;
          overflow: hidden;
        }

        .document-intro::after {
          content: "";
          position: absolute;
          width: 220px;
          height: 220px;
          right: -100px;
          top: -120px;
          border-radius: 50%;
          background: rgba(129,140,248,.25);
          filter: blur(3px);
        }

        .intro-small {
          position: relative;
          z-index: 2;
          color: #a5b4fc;
          font-size: 10px;
          font-weight: 900;
          letter-spacing: 1.4px;
          margin-bottom: 9px;
        }

        .intro-title {
          position: relative;
          z-index: 2;
          font-size: 19px;
          font-weight: 850;
          margin-bottom: 8px;
        }

        .intro-text {
          position: relative;
          z-index: 2;
          max-width: 700px;
          color: #cbd5e1;
          font-size: 13px;
          line-height: 1.8;
          margin: 0;
        }

        .sections {
          padding: 12px 34px 34px;
        }

        .policy-section {
          scroll-margin-top: 95px;
          padding: 31px 0;
          border-bottom: 1px solid #edf0f4;
        }

        .section-heading {
          display: flex;
          gap: 14px;
          align-items: center;
          margin-bottom: 16px;
        }

        .section-number {
          width: 42px;
          height: 42px;
          min-width: 42px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 12px;
          color: #4f46e5;
          background: #eef2ff;
          border: 1px solid #e0e7ff;
          font-size: 10px;
          font-weight: 900;
        }

        .section-title {
          margin: 0;
          color: #111827;
          font-size: 19px;
          letter-spacing: -.3px;
          font-weight: 850;
        }

        .section-text {
          margin: 0 0 14px 56px;
          color: #64748b;
          font-size: 14px;
          line-height: 1.9;
        }

        .points {
          margin: 14px 0 0 56px;
          display: grid;
          gap: 8px;
        }

        .point {
          position: relative;
          padding: 12px 14px 12px 38px;
          border: 1px solid #edf0f4;
          border-radius: 11px;
          background: #fafbfc;
          color: #475569;
          font-size: 13px;
          line-height: 1.6;
        }

        .point::before {
          content: "✓";
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: #eef2ff;
          color: #4f46e5;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 9px;
          font-weight: 900;
        }

        /* SECURITY */

        .security-card {
          margin: 10px 34px 34px;
          padding: 22px;
          display: flex;
          align-items: center;
          gap: 15px;
          border-radius: 17px;
          background: #f8fafc;
          border: 1px solid #e5e7eb;
        }

        .security-icon {
          width: 42px;
          height: 42px;
          min-width: 42px;
          border-radius: 13px;
          background: #111827;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 17px;
        }

        .security-title {
          margin: 0 0 4px;
          color: #111827;
          font-size: 13px;
          font-weight: 850;
        }

        .security-text {
          margin: 0;
          color: #64748b;
          font-size: 12px;
          line-height: 1.6;
        }

        /* CONTACT */

        .contact {
          margin: 0 34px 34px;
          padding: 28px;
          border-radius: 21px;
          background:
            linear-gradient(
              135deg,
              #eef2ff,
              #f5f3ff
            );
          border: 1px solid #e0e7ff;
        }

        .contact-label {
          color: #6366f1;
          font-size: 10px;
          font-weight: 900;
          letter-spacing: 1.4px;
        }

        .contact h2 {
          margin: 7px 0;
          color: #111827;
          font-size: 22px;
          font-weight: 900;
          letter-spacing: -.5px;
        }

        .contact p {
          margin: 0 0 18px;
          color: #64748b;
          font-size: 13px;
          line-height: 1.7;
        }

        .contact-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 10px;
        }

        .contact-item {
          padding: 14px;
          background: rgba(255,255,255,.75);
          border: 1px solid rgba(255,255,255,.9);
          border-radius: 12px;
        }

        .contact-item small {
          display: block;
          margin-bottom: 5px;
          color: #94a3b8;
          font-size: 9px;
          font-weight: 900;
          letter-spacing: 1px;
          text-transform: uppercase;
        }

        .contact-item strong {
          color: #334155;
          font-size: 12px;
          word-break: break-word;
        }

        /* FOOTER */

        .footer {
          padding: 32px 20px;
          background: #080d18;
          text-align: center;
          color: #64748b;
          font-size: 12px;
        }

        .footer strong {
          color: white;
        }

        /* RESPONSIVE */

        @media(max-width: 900px) {
          .privacy-shell {
            grid-template-columns: 1fr;
          }

          .sidebar {
            position: static;
            display: flex;
            overflow-x: auto;
            gap: 4px;
            padding: 10px;
          }

          .sidebar-label {
            display: none;
          }

          .side-item {
            min-width: max-content;
            margin: 0;
          }
        }

        @media(max-width: 650px) {
          .privacy-header {
            height: auto;
          }

          .header-inner {
            padding: 13px 16px;
            flex-direction: column;
            gap: 10px;
          }

          .nav a {
            font-size: 11px;
            padding: 8px 9px;
          }

          .nav .nav-button {
            margin-left: 0;
          }

          .hero {
            padding: 103px 18px 40px;
          }

          .hero h1 {
            font-size: 44px;
            letter-spacing: -2.5px;
          }

          .hero p {
            font-size: 14px;
          }

          .main {
            padding: 0 12px 55px;
          }

          .document {
            border-radius: 20px;
          }

          .document-top {
            padding: 19px;
          }

          .document-intro {
            margin: 18px 18px 5px;
            padding: 21px;
          }

          .sections {
            padding: 5px 19px 20px;
          }

          .policy-section {
            padding: 25px 0;
          }

          .section-title {
            font-size: 17px;
          }

          .section-text {
            margin-left: 0;
            font-size: 13px;
          }

          .points {
            margin-left: 0;
          }

          .security-card {
            margin: 8px 19px 20px;
            padding: 17px;
          }

          .contact {
            margin: 0 19px 20px;
            padding: 21px;
          }

          .contact-grid {
            grid-template-columns: 1fr;
          }
        }

        @media(max-width: 400px) {
          .brand-name {
            font-size: 16px;
          }

          .brand-mark {
            width: 34px;
            height: 34px;
          }

          .hero h1 {
            font-size: 38px;
          }

          .section-number {
            width: 38px;
            height: 38px;
            min-width: 38px;
          }
        }
      `}</style>

      {/* HEADER */}


      {/* HERO */}
      <section className="hero">
        <div className="hero-badge">
          <span className="badge-dot" />
          PRIVACY & SECURITY
        </div>

        <h1>
          Your privacy.
          {/* <br /> */}
          <span>Your trust</span>
        </h1>

        <p>
          We believe privacy should be simple and transparent.
          Here's how School Hub handles your information and
          protects your digital learning experience.
        </p>

        <div className="hero-meta">
          <span>Last Updated January 2025</span>
          <span className="meta-dot" />
          <span>Privacy Policy</span>
        </div>
      </section>

      {/* MAIN */}
      <main className="main">
        <div className="privacy-shell">

          {/* SIDEBAR */}
          <aside className="sidebar">
            <div className="sidebar-label">
              ON THIS PAGE
            </div>

            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className={`side-item ${
                  activeSection === section.id ? "active" : ""
                }`}
              >
                <span className="side-number">
                  {section.id}
                </span>

                <span>{section.title}</span>
              </button>
            ))}

            <button
              onClick={() => scrollToSection("09")}
              className={`side-item ${
                activeSection === "09" ? "active" : ""
              }`}
            >
              <span className="side-number">09</span>
              <span>Contact Us</span>
            </button>
          </aside>

          {/* DOCUMENT */}
          <article className="document">

            <div className="document-top">
              <div className="document-label">
                Privacy Policy
              </div>

              <div className="document-status">
                <span className="status-dot" />
                Active Policy
              </div>
            </div>

            <div className="document-intro">
              <div className="intro-small">
                School Hub • PRIVACY
              </div>

              <div className="intro-title">
                Your information matters to us.
              </div>

              <p className="intro-text">
                This Privacy Policy explains how we collect,
                use, protect and manage your personal information
                when you use our website and educational services.
              </p>
            </div>

            <div className="sections">
              {sections.map((section) => (
                <section
                  key={section.id}
                  id={`privacy-${section.id}`}
                  className="policy-section"
                  onMouseEnter={() => setActiveSection(section.id)}
                >
                  <div className="section-heading">
                    <div className="section-number">
                      {section.id}
                    </div>

                    <h2 className="section-title">
                      {section.title}
                    </h2>
                  </div>

                  <p className="section-text">
                    {section.text}
                  </p>

                  {section.points && (
                    <div className="points">
                      {section.points.map((point, index) => (
                        <div className="point" key={index}>
                          {point}
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              ))}
            </div>

            {/* SECURITY */}
            <div className="security-card">
              <div className="security-icon">
                ♢
              </div>

              <div>
                <h3 className="security-title">
                  Your information is handled responsibly
                </h3>

                <p className="security-text">
                  We take reasonable technical and organizational
                  measures to protect information entrusted to us.
                </p>
              </div>
            </div>

            {/* CONTACT */}
            <section
              id="privacy-09"
              className="contact"
              onMouseEnter={() => setActiveSection("09")}
            >
              <div className="contact-label">
                NEED HELP?
              </div>

              <h2>Contact our support team</h2>

              <p>
                If you have questions or concerns about this
                Privacy Policy, please reach out to us.
              </p>

              <div className="contact-grid">
                <div className="contact-item">
                  <small>Email</small>
                  <strong>
                    support@jomedtuon.com
                  </strong>
                </div>

                <div className="contact-item">
                  <small>Website</small>
                  <strong>
                    www.jomedtuon.com
                  </strong>
                </div>

                <div className="contact-item">
                  <small>Location</small>
                  <strong>India</strong>
                </div>
              </div>
            </section>
          </article>
        </div>
      </main>

      {/* FOOTER */}
   
    </div>
  );
};

export default PrivacyPolicy;